/**
 * Class Name Utilities
 *
 * Utility functions for conditionally joining class names.
 * Alternative to clsx/classnames libraries.
 */

type ClassValue =
  | string
  | number
  | boolean
  | undefined
  | null
  | Record<string, boolean | undefined | null>
  | ClassValue[];

/**
 * Conditionally join class names
 *
 * @example
 * cn('base', isActive && 'active', { hidden: isHidden })
 * // => 'base active hidden' (if all conditions are true)
 */
export function cn(...args: ClassValue[]): string {
  const classes: string[] = [];

  for (const arg of args) {
    if (!arg) continue;

    if (typeof arg === 'string' || typeof arg === 'number') {
      classes.push(String(arg));
    } else if (Array.isArray(arg)) {
      const inner = cn(...arg);
      if (inner) classes.push(inner);
    } else if (typeof arg === 'object') {
      for (const [key, value] of Object.entries(arg)) {
        if (value) classes.push(key);
      }
    }
  }

  return classes.join(' ');
}

/**
 * Merge CSS module classes with additional class names
 *
 * @example
 * const styles = { container: 'Container_abc123' };
 * mergeStyles(styles.container, 'custom-class', { active: true })
 * // => 'Container_abc123 custom-class active'
 */
export function mergeStyles(
  moduleClass: string,
  ...additionalClasses: ClassValue[]
): string {
  return cn(moduleClass, ...additionalClasses);
}

export default cn;
